/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { SUPPORTED_LOCALES } from '../../../../config';
import { DeviceModel } from '../../../../core/device';
import { BrandingUiInfoKeys } from '../../../../core/entities';
import { ServicesClientImpl } from '../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { RestartReasonsOperationsLogger } from '../../../../core/logging';
import { Storage } from '../../../../core/storage/Storage';
import type { TivoPayloadAction } from '../../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../../core/utils';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../../list/list.reducers';
import { showGuideScreen, showUserPreferencesScreen } from '../../../screens/screens.reducers';
import { TAG } from '../../../videoplayerui/onelineguide/onelineguide.constants';
import { IMenuLanguageSelectedOption } from './IMenuLanguageSelectedOption';
import {
    setMenuLanguageOptionsListAction,
    setSelectedMenuLanguageOptionAction,
} from './userprefmenulanguage.actions';
import {
    SET_MENU_LANGUAGE_OPTIONS_LIST,
    SET_MENU_LANGUAGE_SELECTED_OPTION,
} from './userprefmenulanguage.types';

interface IMenuLanguage {
    optionsList: Array<IMenuLanguageSelectedOption>;
    selectedOption?: IMenuLanguageSelectedOption;
}

const initialState: IMenuLanguage = {
    optionsList: [],
};

const userPrefMenuLanguageReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IMenuLanguage>>{},
) => {
    switch (type) {
        case SET_MENU_LANGUAGE_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
            };
        case SET_MENU_LANGUAGE_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        default:
            return state;
    }
};

export const setMenuLanguageOptionsList = () => (dispatch: Function) => {
    const displayName = new ServicesClientImpl().getBrandingUiInfoForKey(
        BrandingUiInfoKeys.DISPLAY_NAME,
    );
    const languageMap = {
        en: Locale.getString(LocaleKeys.SETTINGS_ENGLISH),
        es: Locale.getString(LocaleKeys.SETTINGS_SPANISH),
    };

    const selectedLanguage = languageMap[DeviceModel.getTVProperties().getLang()];
    const settingsItemList: Array<IMenuLanguageSelectedOption> = [
        {
            value: Locale.getString(LocaleKeys.SETTINGS_ENGLISH),
            isSelected: selectedLanguage === Locale.getString(LocaleKeys.SETTINGS_ENGLISH),
            preview: Locale.getString(LocaleKeys.SETTINGS_MENU_LANGUAGE_EN_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
        },
        {
            value: Locale.getString(LocaleKeys.SETTINGS_SPANISH),
            isSelected: selectedLanguage === Locale.getString(LocaleKeys.SETTINGS_SPANISH),
            preview: Locale.getString(LocaleKeys.SETTINGS_MENU_LANGUAGE_ES_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
        },
    ];
    dispatch(setMenuLanguageOptionsListAction(settingsItemList));
    dispatch(setSelectedMenuLanguageOptionAction(settingsItemList[0]));
};

export const resetMenuLanguageOptionsList = () => (dispatch: Function) => {
    dispatch(setSelectedMenuLanguageOptionAction());
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackMenuLanguage = () => (dispatch: Function) => {
    dispatch(setSelectedMenuLanguageOptionAction());
    dispatch(showUserPreferencesScreen());
};

export const onKeyDownMenuLanguage = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.MENU_LANGUAGE]: { position },
        },
        userPrefMenuLanguage: { optionsList },
    } = getState();
    if (position < optionsList.length - 1) {
        dispatch(setSelectedMenuLanguageOptionAction(optionsList[position + 1]));
        dispatch(onKeyDownList(ListTypes.MENU_LANGUAGE));
    }
};

export const onKeyGuideMenuLanguage = () => (dispatch: Function) => {
    dispatch(setSelectedMenuLanguageOptionAction());
    dispatch(showGuideScreen());
};

export const onKeyOkMenuLanguage = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.MENU_LANGUAGE]: { position },
        },
    } = getState();
    const currentAppLanguage = DeviceModel.getTVProperties().getLang();

    if (SUPPORTED_LOCALES[position].language === currentAppLanguage) return;

    Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).setItem(
        Storage.Keys.LANGUAGE,
        SUPPORTED_LOCALES[position].language,
    );
    Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).setItem(
        Storage.Keys.LOCALE,
        SUPPORTED_LOCALES[position].locale,
    );

    const newLang = SUPPORTED_LOCALES[position].language;

    RestartReasonsOperationsLogger.getInstance().updateAppState(
        RestartReasonsOperationsLogger.AppState.NORMAL_USER_EXIT,
    );

    // TODO: We need to see how we can log this event after the app reload.
    Diagnostics.info(TAG, `Language changed to ${newLang}.`);

    DeviceModel.restartApplication();
};

export const onKeyUpMenuLanguage = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.MENU_LANGUAGE]: { position },
        },
        userPrefMenuLanguage: { optionsList },
    } = getState();
    if (position > 0) {
        dispatch(setSelectedMenuLanguageOptionAction(optionsList[position - 1]));
        dispatch(onKeyUpList(ListTypes.MENU_LANGUAGE));
    }
};

export const onMouseClickMenuLanguage = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value.includes(ListTypes.MENU_LANGUAGE)) dispatch(onKeyOkMenuLanguage());
};

export const onMouseOutMenuLanguage = () => (dispatch: Function) => {
    dispatch(onMouseOutList());
};

export const onMouseOverMenuLanguage = () => (dispatch: Function, getState: Function) => {
    dispatch(onMouseOverList());
    const {
        list: {
            [ListTypes.MENU_LANGUAGE]: { position },
        },
        userPrefMenuLanguage: { optionsList },
    } = getState();
    dispatch(setSelectedMenuLanguageOptionAction(optionsList[position]));
};

export default userPrefMenuLanguageReducer;
